import { cleanLigatures } from "./cleanLigatures";
import { ScopedPmd, applyVariablePmd, parsePMD } from "./prosodyMarkdown";
import { PunctuationWhere, punctuationMap } from "./punctuation";
import { NoteClass, PunctuationClass, WordUnit } from "./wordUnit";

export interface ParseUnitOptions {
  /** separate punctuation (default false) */
  readonly punctuation?: boolean;
  /** interpret Prosody Markdown (default false) */
  readonly pmd?: boolean;
  /** make all text lowercase (default false) */
  readonly lowerCase?: boolean;
  /** convert ligatures like ﬁ into standard characters */
  readonly cleanLigatures?: boolean
}

/** interpret punctuation, whitespace, and prosody markdown */
export function parseUnits(
  s: string, abbrevs: Record<string, string>, options?: ParseUnitOptions
): WordUnit[] {
  let results: WordUnit[] = [];
  const doPunct = options && options.punctuation ? true : false;
  const doPMD = options && options.pmd ? true : false;
  const doLowercase = options && options.lowerCase ? true : false;
  const doLigatures = options && options.cleanLigatures ? true : false;

  // separate out stuff that can occur anywhere & break into pieces
  const s2 = doPunct ? s.replace(/–|—|―/g, ' — ') : s;      // only pull out emdashes if doing punctuation
  const s3 = doPunct ? s2.replace(/…|\.\.\./g, ' ; ') : s2; // only pull out ellipses if doing punctuation
  const s4 = s3.replace(/(\n\n\n)|(\n\n)|\n/g, ' \n ');     // always pull out \n
  const s5 = doLigatures ? cleanLigatures(s4) : s4;
  const pieces = s5.split(whitespace);        // words separated by whitespace

  const scopedPmd = new ScopedPmd();
  for (const p of pieces) {
    if (!scopedPmd.isScopedPmd(p)) {
      const units = parsePiece(p, doPunct, doPMD, doLowercase, scopedPmd.useAsIs());
      const processed = scopedPmd.addScopedNotes(units, p);
      results = results.concat(processed);
    }
  }

  if (doPMD) {
    // need to know where punctuation is before applying pmd that varies across sentence
    results = applyVariablePmd(results);
  }

  const fractions = collapseFractions(results);
  const final = collapseAbbrevs(fractions, abbrevs);
  return final;
}
const whitespace = / |\t|\u00a0|\u00ad|\u2000|\u2001|\u2002|\u2003|\u2004|\u2005|\u2006|\u2007|\u2008|\u2009|\u200a|\u202f/;

/** parse a single piece without whitespace that may have attached punctuation & PMD */
function parsePiece(
  piece: string, doPunct: boolean, doPMD: boolean, doLowercase: boolean, useAsIs: boolean
): WordUnit[] {
  if (useAsIs) {
    // skip these checks so as-is gets handled below
  } else if (!doPunct && !doPMD) {
    return unitsFromText(piece, doPunct, doLowercase);
  } else if (doPunct && !doPMD) {
    return parsePunctuation(piece, doLowercase);
  } else if (!doPunct && doPMD) {
    return [parsePMD(piece)];
  }

  const pmd = parsePMD(piece);
  if (useAsIs || (pmd.notes && pmd.notes[0] === NoteClass.AsIs)) {
    const str = pmd.input;
    if (str[str.length - 1] === '/') {  // trim trailing / if present
      return [{ ...pmd, input: str.substring(0, str.length - 1) }];
    }
    return [pmd];
  }
  const punct = parsePunctuation(pmd.input, doLowercase);
  const pmdNotes = pmd.notes;
  if (!pmdNotes) {
    // if no PMD, simply return the results of dealing with punctuation
    return punct;
  }

  // merge PMD notes with any notes on the non-punctuation text content
  const merged = punct.map(p => {
    if (p.punctuation !== undefined) {
      return p; // don't add notes to punctuation
    }
    const notes = p.notes ? pmdNotes.concat(p.notes) : pmdNotes;
    return pmd.tones
      ? { input: p.input, notes, tones: pmd.tones }
      : { input: p.input, notes };
  });

  return merged;
}

/** for text content, adjust text & add notes */
function unitsFromText(content: string, doPunct: boolean, doLowercase: boolean): WordUnit[] {
  if (doPunct) {
    // if /'s separate words, split them & recursively call this function on each piece
    const pieces = content.split("/");
    if (pieces.length > 1) {
      const units: WordUnit[] = pieces.flatMap((p, i) => {
        const unit = unitsFromText(p, doPunct, doLowercase);
        return i === 0 ? unit : [{ input: "/" }].concat(unit);
      });
      return units;
    }
  }

  const notes: NoteClass[] = [];

  if (doPunct) {
    content = content.replace(/’/g, "'"); // normalize contractions
    content = content.replace(/‐|‑/g, "-"); // normalize hyphens

    if (content.indexOf('-') > 0) { // present & not at beginning
      notes.push(NoteClass.Hyphenated);
    }
  }

  if (doLowercase) {
    const lowered = content.toLowerCase();

    // if every character has changed or is a number, it was all caps
    let isAllCaps = true, atLeastOneCap = false;
    for (let i = 0; i < content.length; i++) {
      const same = content[i] === lowered[i];
      if (same && (content[i] < '0' || content[i] > '9')) {
        isAllCaps = false;  // not cap and not a digit
      } else if (!same) {
        atLeastOneCap = true;
      }
    }
    if (isAllCaps && atLeastOneCap) {
      notes.push(NoteClass.AllCaps);
    }

    content = lowered;
  }

  return [notes.length === 0 ? { input: content } : { input: content, notes }];
}

/** parse a single word that may have attached punctuation */
function parsePunctuation(piece: string, doLowercase: boolean): WordUnit[] {
  // first check if we only have one piece of punctuation
  const standAlone = punctuationMap[piece];
  if (standAlone) {
    return [{ input: piece, punctuation: standAlone[0] }];
  }
  // e.g. w/
  if (piece.length === 2 && piece[1] === '/') {
    const input = doLowercase ? piece.toLowerCase() : piece;
    return [{ input }];
  }

  let pre: WordUnit[] = [];
  let post: WordUnit[] = [];

  // check characters at start
  let found: [PunctuationClass, PunctuationWhere] | undefined = undefined;
  do {
    const first = piece[0];
    if (first === '-') {
      break;  // e.g., for negative numbers
    }
    found = punctuationMap[first];
    if (found) {
      const [punctuation, where] = found;
      if (where === PunctuationWhere.WordStart || where === PunctuationWhere.WordBoth) {
        pre.push({ input: first, punctuation });
      }
      piece = piece.substring(1);
    }
  } while (found);

  // check characters at end
  do {
    const last = piece[piece.length - 1];
    found = punctuationMap[last];
    if (found) {
      const [punctuation, where] = found;
      if (where === PunctuationWhere.WordEnd || where === PunctuationWhere.WordBoth) {
        if (punctuation === PunctuationClass.StartQuote) {
          post.push({ input: last, punctuation: PunctuationClass.EndQuote });
        } else if (last === '.' && piece.indexOf('.') !== piece.length - 1) {
          break;  // . at end and in middle -> simply pass thru text as-is
        } else {
          post.push({ input: last, punctuation });
        }
      }
      piece = piece.substring(0, piece.length - 1);
    }
  } while (found);

  // assemble whatever was found
  const results: WordUnit[] = [];
  pre.forEach(p => results.push(p));
  if (piece.length > 0) {
    const units = unitsFromText(piece, true, doLowercase);
    units.forEach(u => results.push(u));
  }
  post.reverse().forEach(p => results.push(p));
  return results;
}

/** check for 3 units that are ## / ## that should be interpreted as a fraction in a single unit */
function collapseFractions(units: WordUnit[]): WordUnit[] {
  if (!units.some(u => u.input === '/')) {
    return units; // if there aren't any /'s, do nothing
  }
  const newUnits: WordUnit[] = [];
  const count = units.length;
  for (let i = 0; i < count; i++) {
    const unit = units[i];
    if (isAllDigits(unit) && (units[i + 1] && units[i + 1].input === '/') && isAllDigits(units[i + 2])) {
      newUnits.push({ ...unit, input: `${unit.input}/${units[i + 2].input}` });
      i += 2;  // skip / and ##
    } else {
      newUnits.push(unit);
    }
  }
  return newUnits;
}
function isAllDigits(unit: WordUnit): boolean {
  return unit !== undefined && unit.input.length > 0 && unit.input.match(/\D/g) === null;
}

/** check for words followed by periods that should be interpreted as an abbreviation & collapse the word/period */
function collapseAbbrevs(units: WordUnit[], abbrevs: Record<string, string>): WordUnit[] {
  const newUnits: WordUnit[] = [];
  const count = units.length;
  for (let i = 0; i < count; i++) {
    const unit = units[i];
    if (i < count - 1 && units[i + 1].input === '.' && abbrevs[unit.input + '.']) {
      // assume it's an abbreviation
      newUnits.push({ ...unit, input: unit.input + '.' });
      i++;  // skip the '.'
    } else {
      newUnits.push(unit);
    }
  }
  return newUnits;
}

/** remove the special stress override symbols */
export function removeProsodyMarkdown(s: string): string {
  // % or * or + or tone or / after space or start of string
  return s.replace(/( %|^%| \*|^\*| \+|^\+| \d\||^\d\|| \\\||^\\\|| \/\||^\/\|| ~\||^~\|| \/|^\/)/g, ' ');
}
